import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Bell,
  Check,
  CheckCircle2,
  FileText,
  Receipt,
  Settings,
  ShoppingCart,
  X,
} from "lucide-react";

import "./NotificationsForm.css";
import { apiCall } from "../api/apiCall";
import { useTranslation } from "../i18n/UseTranslation";
import { getAuth, onAuthStateChanged } from "firebase/auth";

export type NotificationType =
  | "EXPENSE_SUBMITTED"
  | "EXPENSE_APPROVED"
  | "EXPENSE_REJECTED"
  | "EXPENSE_REIMBURSED"
  | "POLICY_UPDATED"
  | "MILEAGE_RATE_UPDATED"
  | "SUBSCRIPTION_PURCHASED";

export interface AppNotification {
  uuid: string;
  type: NotificationType;
  is_read: boolean;
  created_at: string;
}

type NotificationTab = "all" | "unread";

const NotificationsForm: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { t, language } = useTranslation();

  const [activeTab, setActiveTab] = useState<NotificationTab>("all");

  const auth = getAuth();
  const currentUserUid = auth.currentUser?.uid;

  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, () => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const { data: notifications = [], isLoading } = useQuery<
    AppNotification[],
    Error
  >({
    queryKey: ["fetchNotifications"],
    queryFn: () =>
      apiCall<AppNotification[]>("/notifications", {
        method: "GET",
      }),
    enabled: isAuthReady,
  });

  const markAsReadMutation = useMutation({
    mutationFn: (uuid: string) =>
      apiCall(`/notifications/${uuid}/read`, {
        method: "PATCH",
        body: JSON.stringify({
          isRead: true,
        }),
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["fetchNotifications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["fetchUnreadCount"],
      });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => {
      if (!currentUserUid) {
        return Promise.reject(new Error("Utilisateur non connecté"));
      }
      return apiCall(`/users/${currentUserUid}/notifications/read-all`, {
        method: "PATCH",
        body: JSON.stringify({
          isRead: true,
        }),
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["fetchNotifications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["fetchUnreadCount"],
      });
    },
  });

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.is_read).length,
    [notifications],
  );

  const filteredNotifications = useMemo(() => {
    if (activeTab === "all") {
      return notifications;
    }

    return notifications.filter((notification) => !notification.is_read);
  }, [activeTab, notifications]);

  const getNotificationText = (
    notification: AppNotification,
  ): { title: string; message: string } => {
    const dictionary = t.notifications.types;

    const mapTypeToKey: Record<string, string> = {
      EXPENSE_SUBMITTED: "expenseSubmitted",
      EXPENSE_APPROVED: "expenseApproved",
      EXPENSE_REJECTED: "expenseRejected",
      EXPENSE_REIMBURSED: "expenseReimbursed",
      POLICY_UPDATED: "policyUpdated",
      MILEAGE_RATE_UPDATED: "mileageRateUpdated",
      SUBSCRIPTION_PURCHASED: "subscriptionPurchased",
    };

    const translationKey = mapTypeToKey[notification.type];

    return (
      dictionary[translationKey as keyof typeof dictionary] ?? {
        title: t.notifications.title,
        message: notification.type,
      }
    );
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case "EXPENSE_APPROVED":
      case "EXPENSE_REIMBURSED":
        return <CheckCircle2 size={20} className="icon-success" />;

      case "EXPENSE_SUBMITTED":
        return <Receipt size={20} className="icon-neutral" />;

      case "EXPENSE_REJECTED":
        return <AlertCircle size={20} className="icon-danger" />;

      case "POLICY_UPDATED":
      case "MILEAGE_RATE_UPDATED":
        return <FileText size={20} className="icon-info" />;
      case "SUBSCRIPTION_PURCHASED":
        return <ShoppingCart size={20} className="icon-success" />;

      default:
        return <Settings size={20} className="icon-default" />;
    }
  };

  const formatDate = (dateString: string): string => {
    const locale =
      language === "en" ? "en-US" : language === "es" ? "es-ES" : "fr-FR";

    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateString));
  };

  const handleNotificationClick = (notification: AppNotification) => {
    if (notification.is_read || markAsReadMutation.isPending) {
      return;
    }

    markAsReadMutation.mutate(notification.uuid);
  };

  return (
    <main className="dashboard-content notifications-page-container">
      <div className="notifications-header">
        <div>
          <h1 className="notifications-page-title">{t.notifications.title}</h1>

          <p className="page-subtitle">{t.notifications.subtitle}</p>
        </div>

        <div className="header-actions">
          {unreadCount > 0 && (
            <button
              type="button"
              className="mark-all-read-btn"
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isPending}
            >
              <Check size={16} />

              {markAllAsReadMutation.isPending
                ? t.notifications.markingAllAsRead
                : t.notifications.markAllAsRead}
            </button>
          )}

          <button
            type="button"
            className="close-page-btn"
            onClick={() => navigate(-1)}
            title={t.notifications.close}
            aria-label={t.notifications.close}
          >
            <X size={24} />
          </button>
        </div>
      </div>

      <div className="notifications-tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
          onClick={() => setActiveTab("all")}
        >
          {t.notifications.tabs.all}
        </button>

        <button
          type="button"
          className={`tab-btn ${activeTab === "unread" ? "active" : ""}`}
          onClick={() => setActiveTab("unread")}
        >
          {t.notifications.tabs.unread}

          {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
        </button>
      </div>

      <div className="notifications-list">
        {isLoading ? (
          <div className="loading-state">{t.notifications.loading}</div>
        ) : filteredNotifications.length === 0 ? (
          <div className="empty-state notifications-empty-state">
            <Bell size={48} className="notifications-empty-icon" />

            <h3>
              {activeTab === "unread"
                ? t.notifications.noUnreadTitle
                : t.notifications.emptyTitle}
            </h3>

            <p>
              {activeTab === "unread"
                ? t.notifications.noUnreadMessage
                : t.notifications.emptyMessage}
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => {
            const { title, message } = getNotificationText(notification);

            return (
              <article
                key={notification.uuid}
                className={`notification-card ${
                  !notification.is_read ? "is-unread" : ""
                }`}
                onClick={() => handleNotificationClick(notification)}
                role={notification.is_read ? undefined : "button"}
                tabIndex={notification.is_read ? undefined : 0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    handleNotificationClick(notification);
                  }
                }}
              >
                {!notification.is_read && <div className="unread-indicator" />}

                <div className="notification-icon-wrapper">
                  {getNotificationIcon(notification.type)}
                </div>

                <div className="notification-content">
                  <h4 className="notification-title">{title}</h4>

                  <p className="notification-message">{message}</p>

                  <span className="notification-date">
                    {formatDate(notification.created_at)}
                  </span>
                </div>
              </article>
            );
          })
        )}
      </div>
    </main>
  );
};

export default NotificationsForm;
